import { apiClient } from '@/api/client'
import { ApiError, parseProblemDetails } from '@/api/problemDetails'

import type { AcceptedInvitation, CreatedInvitation, PendingInvitation, TeamMember } from './types'

function unexpectedFormatError(status: number): ApiError {
  return new ApiError({
    title: 'A resposta da Api nao tem o formato esperado.',
    status,
    errorCode: 'internal_error',
    correlationId: '',
  })
}

/** Membros do tenant, com nome/e-mail. `null` = sem a capability team.manage (403). */
export async function listMembers(tenantId: string): Promise<readonly TeamMember[] | null> {
  const result = await apiClient.GET('/api/v1/tenants/{tenantId}/members', {
    params: { path: { tenantId } },
  })
  if (result.response.status === 403) {
    return null
  }
  if (!result.response.ok) {
    throw new ApiError(parseProblemDetails(result.error, result.response.status))
  }
  if (result.data === undefined) {
    throw unexpectedFormatError(result.response.status)
  }
  return result.data
}

export async function changeMemberRole(
  tenantId: string,
  membershipId: string,
  role: string,
): Promise<void> {
  const result = await apiClient.PATCH('/api/v1/tenants/{tenantId}/members/{membershipId}', {
    params: { path: { tenantId, membershipId } },
    body: { role },
  })
  if (!result.response.ok) {
    throw new ApiError(parseProblemDetails(result.error, result.response.status))
  }
}

export async function removeMember(tenantId: string, membershipId: string): Promise<void> {
  const result = await apiClient.DELETE('/api/v1/tenants/{tenantId}/members/{membershipId}', {
    params: { path: { tenantId, membershipId } },
  })
  if (!result.response.ok) {
    throw new ApiError(parseProblemDetails(result.error, result.response.status))
  }
}

export async function listInvitations(tenantId: string): Promise<readonly PendingInvitation[]> {
  const result = await apiClient.GET('/api/v1/tenants/{tenantId}/invitations', {
    params: { path: { tenantId } },
  })
  if (!result.response.ok) {
    throw new ApiError(parseProblemDetails(result.error, result.response.status))
  }
  if (result.data === undefined) {
    throw unexpectedFormatError(result.response.status)
  }
  return result.data
}

export async function createInvitation(
  tenantId: string,
  email: string,
  role: string,
): Promise<CreatedInvitation> {
  const result = await apiClient.POST('/api/v1/tenants/{tenantId}/invitations', {
    params: { path: { tenantId } },
    body: { email, role },
  })
  if (!result.response.ok) {
    throw new ApiError(parseProblemDetails(result.error, result.response.status))
  }
  if (result.data === undefined) {
    throw unexpectedFormatError(result.response.status)
  }
  return result.data
}

export async function revokeInvitation(tenantId: string, invitationId: string): Promise<void> {
  const result = await apiClient.POST(
    '/api/v1/tenants/{tenantId}/invitations/{invitationId}/revoke',
    { params: { path: { tenantId, invitationId } } },
  )
  if (!result.response.ok) {
    throw new ApiError(parseProblemDetails(result.error, result.response.status))
  }
}

export async function acceptInvitation(token: string): Promise<AcceptedInvitation> {
  const result = await apiClient.POST('/api/v1/invitations/accept', { body: { token } })
  if (!result.response.ok) {
    throw new ApiError(parseProblemDetails(result.error, result.response.status))
  }
  if (result.data === undefined) {
    throw unexpectedFormatError(result.response.status)
  }
  return result.data
}
